import { AppwriteException, Query } from 'appwrite';

import { appwriteConfig } from '@/config/appwrite';
import { client, databases, storage } from '@/lib/appwrite';
import { profileFromRow, type Profile, type ProfileRow } from './model';

/**
 * Read-only access to the `profiles` collection.
 *
 * There is deliberately no create or update here: ELA Lab shows the profile
 * the ela app maintains, and the row's permissions
 * (`read`/`update`/`delete` for the owning user) are not something this
 * viewer should exercise beyond the read.
 */

/**
 * Loads the signed-in user's profile, or `null` when they have none.
 *
 * The document id equals the user id in this collection, so the direct read is
 * one request. A row written with a generated id would still be found by the
 * `user_id` query, which is why the fallback exists rather than assuming the
 * convention holds forever.
 */
export async function fetchProfile(userId: string): Promise<Profile | null> {
  try {
    const document = await databases.getDocument({
      databaseId: appwriteConfig.databaseId,
      collectionId: appwriteConfig.profilesCollectionId,
      documentId: userId,
    });
    return profileFromRow(document as unknown as ProfileRow);
  } catch (error) {
    if (!(error instanceof AppwriteException)) throw error;

    // 404 → no row under that id. 401/403 → the row exists but is not readable
    // under this id, which the user_id query may still resolve.
    if (error.code !== 404 && error.code !== 401 && error.code !== 403) throw error;
  }

  try {
    const result = await databases.listDocuments({
      databaseId: appwriteConfig.databaseId,
      collectionId: appwriteConfig.profilesCollectionId,
      queries: [Query.equal('user_id', userId), Query.limit(1)],
    });
    const first = result.documents[0];
    return first === undefined ? null : profileFromRow(first as unknown as ProfileRow);
  } catch (error) {
    if (error instanceof AppwriteException && (error.code === 404 || error.code === 401 || error.code === 403)) {
      // No profile is a normal state, not an error worth surfacing.
      return null;
    }
    throw error;
  }
}

/**
 * Downloads a profile image and returns an object URL for it.
 *
 * The image is fetched as bytes rather than pointed at with
 * `storage.getFileView()` in an `<img src>`: that URL is cross-origin and
 * carries no credentials on an image request, so it would 401 for any browser
 * blocking third-party cookies. `client.call` sends the session the same way
 * the rest of the SDK does.
 *
 * The caller owns the returned URL and must revoke it.
 */
export async function fetchProfileImageUrl(fileId: string): Promise<string> {
  const url = new URL(storage.getFileView(appwriteConfig.profileImagesBucketId, fileId));
  const result: unknown = await client.call('GET', url, {}, {}, 'arrayBuffer');

  const blob = toBlob(result);
  if (blob === null) throw new Error('Unexpected response while loading the profile image.');
  return URL.createObjectURL(blob);
}

/** Normalises the several shapes a runtime may hand back for binary data. */
function toBlob(result: unknown): Blob | null {
  if (result instanceof Blob) return result;
  if (result instanceof ArrayBuffer) return new Blob([result]);
  if (ArrayBuffer.isView(result)) {
    // Copy into a plain Uint8Array: the view's buffer may be a
    // SharedArrayBuffer, which Blob does not accept.
    return new Blob([new Uint8Array(result.buffer as ArrayBuffer, result.byteOffset, result.byteLength)]);
  }
  return null;
}
